import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Zap, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * BillingSuccess — shown at /billing/success after a successful Stripe Checkout.
 * Stripe redirects here with ?session_id=... (we don't need it on the frontend;
 * the webhook already activated the Pro plan server-side).
 */
export default function BillingSuccess() {
  const navigate = useNavigate();

  // Auto-redirect to dashboard after 6 seconds
  useEffect(() => {
    const timer = setTimeout(() => navigate('/dashboard', { replace: true }), 6000);
    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#030712] text-white flex items-center justify-center p-6">
      <div className="fixed inset-0 hero-glow" />

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="relative z-10 text-center max-w-md"
      >
        {/* Animated tick */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="flex justify-center mb-6"
        >
          <div className="w-24 h-24 rounded-full bg-[#00E599]/10 border-2 border-[#00E599]/40 flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-[#00E599]" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h1 className="text-3xl font-bold mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            You're now on Pro! 🎉
          </h1>
          <p className="text-slate-400 mb-6">
            Your payment was successful. All AI features are now unlocked with
            no limits. Welcome to AutoQA Pro.
          </p>

          {/* Feature pills */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {['Unlimited AI generation', 'Unlimited test runs', 'Visual regression', 'API testing', 'Priority support'].map(f => (
              <span
                key={f}
                className="px-3 py-1 rounded-full bg-[#00E599]/10 border border-[#00E599]/20 text-[#00E599] text-xs font-medium"
              >
                {f}
              </span>
            ))}
          </div>

          <Button
            className="bg-[#00E599] text-black hover:bg-[#00CC88] font-bold h-12 px-8"
            onClick={() => navigate('/dashboard', { replace: true })}
          >
            <Zap className="w-5 h-5 mr-2" />
            Go to Dashboard
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>

          <p className="text-slate-600 text-xs mt-4">
            Redirecting automatically in a few seconds…
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}
